import Image from 'next/image'
import { ImageBlock as ImageBlockType } from '@/payload-types'
import { Media } from '../Media'
import { MediaProps } from '../Media/types'
import { cn } from '@/lib/utils'
import { AspectRatio } from '@/components/ui/aspect-ratio'
import { Fragment, ReactNode } from 'react'

type ImageBlockProps = {
  imageBlock?: ImageBlockType | null
  /**
   * Skip the AspectRatio wrapper and let the image fill its parent instead.
   * Use when the parent is already `relative` and has a fixed size – otherwise
   * the AspectRatio wrapper fights the parent's height and the image is pinned
   * to the top on wide screens.
   */
  disableAspectRatio?: boolean
} & Omit<MediaProps, 'resource' | 'alt'>

export function ImageBlock({ imageBlock, disableAspectRatio, ...mediaProps }: ImageBlockProps) {
  const Wrapper = ({ children }: { children: ReactNode }) =>
    disableAspectRatio ? (
      <Fragment>{children}</Fragment>
    ) : (
      <AspectRatio ratio={mediaProps.ratio}>{children}</AspectRatio>
    )

  if (!imageBlock)
    return (
      <Wrapper>
        <div className={cn('bg-stone-300 w-full h-full', mediaProps.className)} />
      </Wrapper>
    )

  const alt = imageBlock.alt || ''

  if (imageBlock.type === 'external' && imageBlock.external) {
    return (
      <Wrapper>
        <Image
          src={imageBlock.external}
          loading={mediaProps.loading ?? (mediaProps.priority ? undefined : 'lazy')}
          priority={mediaProps.priority}
          alt={alt}
          fill
          sizes={mediaProps.size}
          quality={75}
          placeholder='blur'
          blurDataURL='data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9IiNkNmQzZDEiLz48L3N2Zz4='
          className={cn(mediaProps.imgClassName, 'object-cover')}
          {...mediaProps.onClick}
        />
      </Wrapper>
    )
  }

  if (imageBlock.type === 'internal' && imageBlock.internal) {
    return (
      <Wrapper>
        <Media resource={imageBlock.internal} alt={alt} {...mediaProps} />
      </Wrapper>
    )
  }

  return (
    <Wrapper>
      <div className={cn('bg-stone-300 w-full h-full', mediaProps.className)} />
    </Wrapper>
  )
}
